import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { Plus, CalendarIcon, X, Search, Mail, PenLine, UserX, Trash2 } from 'lucide-react';
import api from '../utils/api';
import { formatCurrency, formatDate, getStatusLabel, getStatusColor } from '../utils/helpers';
import { toast } from 'sonner';
import { useConfirm } from '../context/ConfirmContext';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

export const WorkOrdersPage = () => {
  const { user } = useAuth();
  const confirm = useConfirm();
  const canDelete = user?.role === 'ADMIN';
  const [workOrders, setWorkOrders] = useState([]);
  const [allCustomers, setAllCustomers] = useState([]);
  const [customers, setCustomers] = useState({});
  const [sites, setSites] = useState({});
  const [statusFilter, setStatusFilter] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerOpen, setCustomerOpen] = useState(false);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [datePreset, setDatePreset] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const loadCustomers = useCallback(async () => {
    try {
      const response = await api.getCustomers();
      setAllCustomers(response.data);
      
      // Create a map of customer_id -> customer name
      const customersMap = {};
      response.data.forEach(c => {
        customersMap[c.id] = c.name_or_company;
      });
      setCustomers(customersMap);
      
      // Load sites for all customers
      const sitesMap = {};
      for (const customer of response.data) {
        try {
          const sitesResponse = await api.getCustomerSites(customer.id);
          sitesResponse.data.forEach(s => {
            sitesMap[s.id] = s.site_name;
          });
        } catch (e) {
          // Ignore errors for individual customer sites (don't block the whole list)
          console.warn(`[load-sites] customer ${customer.id} sites fetch failed:`, e);
        }
      }
      setSites(sitesMap);
    } catch (error) {
      console.error('Failed to load customers:', error);
    }
  }, []);

  const loadWorkOrders = useCallback(async () => {
    try {
      const response = await api.getWorkOrders({});
      setWorkOrders(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento bollettini');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCustomers();
    loadWorkOrders();
  }, [loadCustomers, loadWorkOrders]);

  // Apply date preset
  const applyDatePreset = (preset) => {
    const now = new Date();
    let from = null;
    
    switch (preset) {
      case '7d':
        from = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case '1m':
        from = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
        break;
      case '3m':
        from = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
        break;
      case '6m':
        from = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
        break;
      case '12m':
        from = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
        break;
      default:
        from = null;
    }
    
    setDatePreset(preset);
    setDateFrom(from);
    setDateTo(preset ? now : null);
  };

  // Clear all filters
  const clearFilters = () => {
    setStatusFilter('');
    setCustomerFilter('');
    setCustomerSearch('');
    setDateFrom(null);
    setDateTo(null);
    setDatePreset('');
  };

  const handleDelete = async (wo, e) => {
    e.stopPropagation();
    const ok = await confirm({
      title: `Elimina bollettino ${wo.number}?`,
      description: 'Questa operazione cancella DEFINITIVAMENTE il bollettino e tutte le sue righe. Non è recuperabile.',
      confirmText: 'Elimina definitivamente',
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.deleteWorkOrder(wo.id);
      toast.success('Bollettino eliminato');
      loadWorkOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'eliminazione");
    }
  };

  // Filter work orders
  const filteredWorkOrders = useMemo(() => {
    return workOrders.filter(wo => {
      // Status filter
      if (statusFilter && wo.status !== statusFilter) return false;
      
      // Customer filter
      if (customerFilter && wo.customer_id !== customerFilter) return false;
      
      // Date filter
      if (dateFrom || dateTo) {
        const woDate = new Date(wo.created_at);
        if (dateFrom && woDate < dateFrom) return false;
        if (dateTo) {
          const endOfDay = new Date(dateTo);
          endOfDay.setHours(23, 59, 59, 999);
          if (woDate > endOfDay) return false;
        }
      }
      
      return true;
    });
  }, [workOrders, statusFilter, customerFilter, dateFrom, dateTo]);

  // Filtered customers for search
  const filteredCustomers = useMemo(() => {
    if (!customerSearch) return allCustomers;
    return allCustomers.filter(c => 
      c.name_or_company.toLowerCase().includes(customerSearch.toLowerCase())
    );
  }, [allCustomers, customerSearch]);

  // Check if any filter is active
  const hasActiveFilters = statusFilter || customerFilter || dateFrom || dateTo;

  // Format user name as "Name S." (first name + first letter of surname)
  const formatUserName = (fullName) => {
    if (!fullName) return '-';
    const parts = fullName.trim().split(' ');
    if (parts.length === 1) return parts[0];
    const firstName = parts[0];
    const lastNameInitial = parts[parts.length - 1].charAt(0).toUpperCase();
    return `${firstName} ${lastNameInitial}.`;
  };

  // Get empty message based on filters
  const getEmptyMessage = () => {
    if (customerFilter && statusFilter) {
      return `Nessun bollettino "${getStatusLabel(statusFilter)}" per ${customers[customerFilter]}`;
    }
    if (customerFilter) {
      return `Nessun bollettino per ${customers[customerFilter]}`;
    }
    if (statusFilter) {
      return `Nessun bollettino con stato "${getStatusLabel(statusFilter)}"`;
    }
    if (dateFrom || dateTo) {
      return 'Nessun bollettino nel periodo selezionato';
    }
    return 'Nessun bollettino trovato';
  };

  return (
    <div data-testid="work-orders-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-4xl font-bold text-slate-900 mb-2">Bollettini di Lavoro</h1>
          <p className="text-slate-600">Gestisci i bollettini e il workflow di approvazione</p>
        </div>
        <Button onClick={() => navigate('/app/work-orders/new')} data-testid="create-work-order-btn">
          <Plus size={18} className="mr-2" />
          Nuovo Bollettino
        </Button>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            {/* Status Filter */}
            <div className="w-48">
              <Label className="text-xs text-slate-500 mb-1 block">Stato</Label>
              <Select value={statusFilter || '__all__'} onValueChange={(val) => setStatusFilter(val === '__all__' ? '' : val)}>
                <SelectTrigger data-testid="status-filter">
                  <SelectValue placeholder="Tutti gli stati" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Tutti gli stati</SelectItem>
                  <SelectItem value="DRAFT">Bozza</SelectItem>
                  <SelectItem value="IN_REVIEW">In Revisione</SelectItem>
                  <SelectItem value="NEEDS_CORRECTION">Da Correggere</SelectItem>
                  <SelectItem value="APPROVED">Approvato</SelectItem>
                  <SelectItem value="INVOICED">Fatturato</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Customer Filter with Search */}
            <div className="w-64">
              <Label className="text-xs text-slate-500 mb-1 block">Cliente</Label>
              <Popover open={customerOpen} onOpenChange={setCustomerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={customerOpen}
                    className="w-full justify-between font-normal"
                    data-testid="customer-filter"
                  >
                    {customerFilter ? customers[customerFilter] : "Tutti i clienti"}
                    <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-0" align="start">
                  <Command>
                    <CommandInput 
                      placeholder="Cerca cliente..." 
                      value={customerSearch}
                      onValueChange={setCustomerSearch}
                    />
                    <CommandList>
                      <CommandEmpty>Nessun cliente trovato</CommandEmpty>
                      <CommandGroup>
                        <CommandItem
                          value="__all__"
                          onSelect={() => {
                            setCustomerFilter('');
                            setCustomerSearch('');
                            setCustomerOpen(false);
                          }}
                        >
                          Tutti i clienti
                        </CommandItem>
                        {filteredCustomers.map(customer => (
                          <CommandItem
                            key={customer.id}
                            value={customer.name_or_company}
                            onSelect={() => {
                              setCustomerFilter(customer.id);
                              setCustomerSearch('');
                              setCustomerOpen(false);
                            }}
                          >
                            {customer.name_or_company}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Date From */}
            <div className="w-40">
              <Label className="text-xs text-slate-500 mb-1 block">Data da</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start font-normal"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dateFrom ? format(dateFrom, 'dd.MM.yyyy') : 'Seleziona'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={dateFrom}
                    onSelect={(date) => {
                      setDateFrom(date);
                      setDatePreset('');
                    }}
                    locale={it}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Date To */}
            <div className="w-40">
              <Label className="text-xs text-slate-500 mb-1 block">Data a</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start font-normal"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dateTo ? format(dateTo, 'dd.MM.yyyy') : 'Seleziona'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={dateTo}
                    onSelect={(date) => {
                      setDateTo(date);
                      setDatePreset('');
                    }}
                    locale={it}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Date Presets */}
            <div className="w-36">
              <Label className="text-xs text-slate-500 mb-1 block">Periodo</Label>
              <Select value={datePreset || '__none__'} onValueChange={(val) => applyDatePreset(val === '__none__' ? '' : val)}>
                <SelectTrigger>
                  <SelectValue placeholder="Periodo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">-</SelectItem>
                  <SelectItem value="7d">Ultimi 7 giorni</SelectItem>
                  <SelectItem value="1m">Ultimo mese</SelectItem>
                  <SelectItem value="3m">Ultimi 3 mesi</SelectItem>
                  <SelectItem value="6m">Ultimi 6 mesi</SelectItem>
                  <SelectItem value="12m">Ultimo anno</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-slate-500 hover:text-slate-700"
              >
                <X size={16} className="mr-1" />
                Azzera filtri
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Work Orders List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>
              Bollettini
              {filteredWorkOrders.length !== workOrders.length && (
                <span className="text-sm font-normal text-slate-500 ml-2">
                  ({filteredWorkOrders.length} di {workOrders.length})
                </span>
              )}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-16 bg-slate-100 animate-pulse rounded"></div>
              ))}
            </div>
          ) : filteredWorkOrders.length === 0 ? (
            <div className="text-center py-12">
              <Plus size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-600 mb-2">{getEmptyMessage()}</p>
              {hasActiveFilters && (
                <Button variant="outline" size="sm" onClick={clearFilters} className="mt-2">
                  Azzera filtri
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-secondary border-b">
                  <tr>
                    <th className="text-left p-4 text-sm font-semibold text-slate-700">Numero</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-700">Cliente</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-700">Progetto</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-700">Creato da</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-700">Stato</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-700">Data</th>
                    {user.role !== 'EMPLOYEE' && (
                      <th className="text-right p-4 text-sm font-semibold text-slate-700">Totale</th>
                    )}
                    {canDelete && (
                      <th className="text-right p-4 text-sm font-semibold text-slate-700 w-12"></th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {filteredWorkOrders.map(wo => (
                    <tr
                      key={wo.id}
                      className="border-b table-row-hover"
                      onClick={() => navigate(`/app/work-orders/${wo.id}`)}
                      data-testid={`work-order-row-${wo.id}`}
                    >
                      <td className="p-4">
                        <div className="font-mono font-medium text-slate-900">{wo.number}</div>
                      </td>
                      <td className="p-4 text-slate-700 font-medium">
                        {customers[wo.customer_id] || 'Cliente'}
                      </td>
                      <td className="p-4 text-slate-600">
                        {wo.site_id ? sites[wo.site_id] || '-' : '-'}
                      </td>
                      <td className="p-4 text-slate-600 text-sm">
                        {wo.on_behalf_of_name ? (
                          <div>
                            <div>{formatUserName(wo.on_behalf_of_name)}</div>
                            <div className="text-[11px] text-amber-700">via {formatUserName(wo.created_by_name)}</div>
                          </div>
                        ) : (
                          formatUserName(wo.created_by_name)
                        )}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <span className={`status-badge ${getStatusColor(wo.status)}`}>
                            {getStatusLabel(wo.status)}
                          </span>
                          {wo.signature_base64 && (
                            <PenLine size={14} className="text-green-600" title={wo.signer_name ? `Firmato da ${wo.signer_name}` : 'Firmato'} />
                          )}
                          {!wo.signature_base64 && wo.customer_not_present && (
                            <UserX size={14} className="text-amber-600" title="Cliente non presente" />
                          )}
                          {wo.email_sent_at && (
                            <Mail size={14} className="text-green-500" title={wo.email_sent_to ? `Inviato a ${wo.email_sent_to}` : 'Email inviata'} />
                          )}
                        </div>
                      </td>
                      <td className="p-4 text-slate-600">{formatDate(wo.created_at)}</td>
                      {user.role !== 'EMPLOYEE' && (
                        <td className="p-4 text-right font-mono">
                          {wo.gross_total ? formatCurrency(wo.gross_total) : '-'}
                        </td>
                      )}
                      {canDelete && (
                        <td className="p-4 text-right">
                          {wo.status === 'CANCELLED' ? (
                            <button
                              type="button"
                              onClick={(e) => handleDelete(wo, e)}
                              className="p-1.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-600 transition-colors"
                              title="Elimina definitivamente"
                              data-testid={`delete-workorder-${wo.id}`}
                            >
                              <Trash2 size={16} />
                            </button>
                          ) : null}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
